/** @jsxImportSource react */
'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useInterval } from 'usehooks-ts';
import { useCurrentAccountId } from '../IdentityProvider/useCurrentAccountId';
import { useSsoHint } from '../IdentityProvider/useSsoHint';
import { useDocumentIsHidden } from '../utils/useDocumentIsHidden';
import { useJWTRefresh } from './useJWTRefresh';
import { fetchSessionCookieStatus } from '../client/sessionCookieStatus';
import { buildLoginUrl } from '../utils/buildLoginUrl';
import { SessionState, SessionStatus } from './types';
import { JWT_REFRESH_INTERVAL, LOGIN_URL } from '../constants';
import { markSessionReady, closeSessionReady } from '../sessionReady';

// Safe default when there's no provider above: nothing known, so `enabled: isAuthed` stays off.
export const DEFAULT_SESSION_STATE: SessionState = {
  status: SessionStatus.Loading,
  isAuthed: false,
  isValidating: false,
  isLoading: false,
};

export const SessionContext = createContext<SessionState>(
  DEFAULT_SESSION_STATE
);

interface SessionProviderProps {
  children: ReactNode;
  /**
   * Withhold `children` (showing `fallback`) until the session resolves; a settled
   * Unauthenticated redirects to login (subject to `redirectOnUnauthenticated`).
   * Off by default.
   */
  blockRenderUntilVerified?: boolean;
  /** Rendered while `blockRenderUntilVerified` is holding the session. Defaults to null. */
  fallback?: ReactNode;
  /**
   * Whether a settled Unauthenticated session redirects to login, from the mount
   * verification and from `blockRenderUntilVerified`. On by default; set false for
   * apps that are themselves the login/signup surface, where an anonymous visitor
   * is the expected case rather than something to redirect away from.
   */
  redirectOnUnauthenticated?: boolean;
}

/**
 * Keeps the JWT fresh and exposes auth state via useSession() (`status` +
 * `isValidating`). The interval and account/ssohint-change refreshes are plain
 * refreshes; the mount refresh runs alongside a cheap session-cookie check.
 */
export const SessionProvider = ({
  children,
  blockRenderUntilVerified = false,
  fallback = null,
  redirectOnUnauthenticated = true,
}: SessionProviderProps) => {
  // account/ssohint identify the session; visibility pauses work while hidden.
  const currentAccountId = useCurrentAccountId();
  const ssoHint = useSsoHint();
  const isHidden = useDocumentIsHidden();

  // Never redirect the login page to itself: buildLoginUrl() would otherwise nest
  // an ever-growing `next` param back onto the page that's already showing.
  const redirectToLogin = useCallback(() => {
    if (!redirectOnUnauthenticated || window.location.pathname === LOGIN_URL) {
      return;
    }
    window.location.href = buildLoginUrl();
  }, [redirectOnUnauthenticated]);

  const refresh = useJWTRefresh();
  const [status, setStatus] = useState<SessionStatus>(SessionStatus.Loading);

  // Cheap-check in-flight, so isValidating spans the whole mount decision.
  const [checkInFlight, setCheckInFlight] = useState(false);

  const isValidating = refresh.isValidating || checkInFlight;

  // Refresh and fold the outcome into status. Drives the interval and account/ssohint
  // -change refreshes; the mount is handled by the verify effect below.
  const runRefresh = useCallback(async () => {
    const outcome = await refresh.performRefresh();
    // null = inconclusive; keep the last settled status.
    if (outcome) {
      setStatus(outcome);
    }
  }, [refresh.performRefresh]);

  // Refresh on account/ssohint change. Skip the first run; the mount is handled by
  // the verify effect below.
  const changeMountedRef = useRef(false);
  const lastIdentityRef = useRef<string | null>(null);
  useEffect(() => {
    const firstRun = !changeMountedRef.current;
    changeMountedRef.current = true;
    if (isHidden) {
      return;
    }
    const identity = `${currentAccountId}|${ssoHint}`;
    if (firstRun) {
      lastIdentityRef.current = identity;
      return;
    }
    // Re-close the readiness gate only when the identity actually changed (a cross-tab
    // account switch re-verifies in place with no reload), not on a plain visibility-
    // driven refresh. The markSessionReady effect below reopens it once this refresh
    // settles authed, so callers block against the outgoing account's JWT in between.
    if (identity !== lastIdentityRef.current) {
      lastIdentityRef.current = identity;
      closeSessionReady();
    }
    runRefresh();
  }, [currentAccountId, ssoHint, isHidden, runRefresh]);

  // Mount: run the cheap check and refresh together. A 200 from either marks the
  // session authenticated, and a cheap 200 holds even if the refresh later fails.
  // Only a conclusive refresh failure (401/403) marks it unauthenticated and redirects
  // to login; an inconclusive refresh leaves the status unchanged.
  useEffect(() => {
    if (isHidden) {
      return;
    }
    if (ssoHint === 'anonymous') {
      setStatus(SessionStatus.Unauthenticated);
      return;
    }

    let cancelled = false;
    let cheapConfirmed = false;

    setCheckInFlight(true);
    const cheapCheck = fetchSessionCookieStatus();
    const refreshOutcome = refresh.performRefresh();

    cheapCheck
      .then(cookieStatus => {
        if (!cancelled && cookieStatus === 200) {
          cheapConfirmed = true;
          setStatus(SessionStatus.Authenticated);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setCheckInFlight(false);
        }
      });

    Promise.all([cheapCheck, refreshOutcome]).then(([, outcome]) => {
      if (cancelled || cheapConfirmed) {
        return;
      }
      if (outcome === SessionStatus.Authenticated) {
        setStatus(SessionStatus.Authenticated);
        return;
      }
      if (outcome === SessionStatus.Unauthenticated) {
        setStatus(SessionStatus.Unauthenticated);
        redirectToLogin();
      }
    });

    return () => {
      cancelled = true;
    };
    // Mount-only: capture the mount-time signed-in/visibility snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Blocking + a settled Unauthenticated → login (covers the anonymous short-circuit).
  useEffect(() => {
    if (blockRenderUntilVerified && status === SessionStatus.Unauthenticated) {
      redirectToLogin();
    }
  }, [blockRenderUntilVerified, status, redirectToLogin]);

  // Keep the JWT fresh on a cadence; paused while hidden.
  useInterval(
    () => {
      runRefresh();
    },
    isHidden ? null : JWT_REFRESH_INTERVAL
  );

  // Public session state; memoized so consumers re-render only on real changes.
  const session = useMemo<SessionState>(
    () => ({
      status,
      isAuthed: status === SessionStatus.Authenticated,
      isValidating,
      isLoading: status === SessionStatus.Loading && isValidating,
    }),
    [status, isValidating]
  );

  // Open the imperative readiness gate (sessionJwtIsAvailable) once the session is authed
  // and settled, so non-React callers (tRPC links, raw fetch, axios interceptors) can
  // await verification. This also reopens the gate after an identity change re-closes it
  // (see the account/ssohint change effect above), once the new session settles authed.
  useEffect(() => {
    if (session.isAuthed && !session.isValidating) {
      markSessionReady();
    }
  }, [session.isAuthed, session.isValidating]);

  const blocked =
    blockRenderUntilVerified && status !== SessionStatus.Authenticated;

  return (
    <SessionContext.Provider value={session}>
      {blocked ? fallback : children}
    </SessionContext.Provider>
  );
};
